/**
 * Presentational view-models for the Workers feature.
 *
 * Hook-free, serialisable shapes so the listing + detail components render in
 * Storybook from fixtures. The containers map the live `TenantWorkerVM`
 * (from `useWorkerArrangements`) onto these.
 */

/** Worker arrangement type — Employee or Contractor (server enum). */
export type WorkerType = 'EMPLOYEE' | 'CONTRACTOR' | string;

/** One row in the Workers listing table. */
export interface WorkerRowVM {
  /** Stable arrangement id — used as the detail nav param. */
  id: string;
  /** Canonical person id — the identity handle. */
  canonicalPersonId: string;
  /** Resolved person name (R61: now available from WorkerArrangementModel). */
  name?: string | null;
  type: WorkerType;
  role: string | null;
  title: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

/** Identity + terms summary shown across the worker detail header + Overview. */
export interface WorkerDetailVM {
  id: string;
  canonicalPersonId: string;
  naturalPersonPartyId: string | null;
  /** Resolved person name (best-effort); null when unresolved. */
  name?: string | null;
  type: WorkerType;
  role: string | null;
  title: string | null;
  terms: string | null;
  jobDescription?: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
}

/** Human label for a worker type. */
export function workerTypeLabel(type: WorkerType): string {
  switch (type) {
    case 'EMPLOYEE':
      return 'Employee';
    case 'CONTRACTOR':
      return 'Contractor';
    default:
      return type;
  }
}
